import { useCallback, useEffect, useRef, useState } from 'react'
import { getLocalAndroidAll } from 'src/@api/dashboard'
import { useDeviceScopedInit } from 'src/hooks/useDeviceScopedInit'
import { readLocalDeviceUdid, readUserEmail } from 'src/utils/readAndroidLocalContext'

/**
 * Android Local 图片/截图分页列表 Hook（滚动触底加载），设备切换时隔离旧响应。
 * 日期筛选通过 params.startTime / endTime（毫秒）走接口重拉，非本地过滤。
 *
 * @param {Object} options
 * @param {string} options.category - 接口 category，如 INSTAGRAM_SCREENSHOT
 * @param {number} options.limit - 每页条数
 * @returns {{ imgData: Array, loading: boolean, initialLoading: boolean, total: number, setDateRange: Function }}
 */
export function useAndroidLocalImageList({ category, limit }) {
  const [imgData, setImgData] = useState([])
  const [loading, setLoading] = useState(true)
  const [hasLoaded, setHasLoaded] = useState(false)
  const [total, setTotal] = useState(0)
  const scopeRef = useRef(null)
  const scrollContainerRef = useRef(null)
  const pageRef = useRef({ offset: 0, maxNum: false, loading: false })

  /** @type {React.MutableRefObject<{ startTime: number, endTime: number } | null>} */
  const dateRangeRef = useRef(null)
  const fetchGenRef = useRef(0)

  const loadPage = useCallback(
    async (offset, scope = scopeRef.current) => {
      if (!scope || pageRef.current.loading || pageRef.current.maxNum) return

      const gen = fetchGenRef.current
      pageRef.current.loading = true
      const { context, signal, isCurrent } = scope

      try {
        const params = {
          category,
          deviceUdid: readLocalDeviceUdid(context),
          offset,
          limit
        }
        const range = dateRangeRef.current
        if (range?.startTime != null) params.startTime = range.startTime
        if (range?.endTime != null) params.endTime = range.endTime

        const result = await getLocalAndroidAll(
          {
            username: readUserEmail(),
            params
          },
          { signal }
        )

        if (!isCurrent() || gen !== fetchGenRef.current) return

        const contents = result?.data?.contents
        if (contents) {
          setImgData(old => (offset === 0 ? [...contents] : [...old, ...contents]))
          setTotal(result.data.totalCount)
          pageRef.current.offset = offset
          if (contents.length < limit) pageRef.current.maxNum = true
        } else {
          if (offset === 0) {
            setImgData([])
            setTotal(0)
          }
          pageRef.current.maxNum = true
        }
        setHasLoaded(true)
        setLoading(false)
      } catch (error) {
        if (isCurrent() && gen === fetchGenRef.current && error?.code !== 'ERR_CANCELED') {
          console.error('Error fetching image data:', error)
          setHasLoaded(true)
          setLoading(false)
        }
      } finally {
        if (isCurrent() && gen === fetchGenRef.current) pageRef.current.loading = false
      }
    },
    [category, limit]
  )

  const reloadFromStart = useCallback(() => {
    const scope = scopeRef.current
    if (!scope) return

    fetchGenRef.current += 1
    pageRef.current = { offset: 0, maxNum: false, loading: false }
    setLoading(true)
    loadPage(0, scope)
  }, [loadPage])

  /**
   * @param {{ startTime: number, endTime: number } | null} range - 毫秒时间戳；null 清除筛选
   */
  const setDateRange = useCallback(
    range => {
      dateRangeRef.current = range
      reloadFromStart()
    },
    [reloadFromStart]
  )

  // 设备切换：清空列表与日期筛选并重新加载第一页
  useDeviceScopedInit(
    useCallback(
      (_, scope) => {
        scopeRef.current = scope
        dateRangeRef.current = null
        fetchGenRef.current += 1
        pageRef.current = { offset: 0, maxNum: false, loading: false }
        setImgData([])
        setHasLoaded(false)
        setLoading(true)
        setTotal(0)
        loadPage(0, scope)
      },
      [loadPage]
    )
  )

  useEffect(() => {
    const handleScroll = () => {
      const page = pageRef.current
      const container = scrollContainerRef.current
      const isAtBottom = container
        ? container.scrollTop + container.clientHeight >= container.scrollHeight - 100
        : window.scrollY + window.innerHeight >= document.documentElement.scrollHeight - 100

      if (isAtBottom && !page.maxNum && !page.loading) {
        loadPage(page.offset + limit)
      }
    }

    const scrollContainer = scrollContainerRef.current
    const scrollTarget = scrollContainer || window
    scrollTarget.addEventListener('scroll', handleScroll)

    return () => scrollTarget.removeEventListener('scroll', handleScroll)
  }, [hasLoaded, limit, loadPage])

  return {
    imgData,
    loading,
    initialLoading: loading && !hasLoaded,
    total,
    setDateRange,
    scrollContainerRef
  }
}
